import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcFeesV1FeeRateQueryServiceFeeRateGet } from '@interxfi/api-client/generated/hooks/ixc-fees-v1-fee-rate-query-service-fee-rate-get';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Typography } from '@interxfi/design-system/components/typography';
import EditIcon from '@interxfi/design-system/icons/react/icon-edit';
import { type IxcFeesV1FeeRateState, IxcFeesV1FeeRateStatus, type IxcFeesV1FeeType } from '@interxfi/types';
import { FeeRateEventDrawer, FeeRateEventSummary } from '../../../../../../../../../../../../features/fees/fee-rate/fee-rate-event';
import { getFeeRateStateListItems } from '../../../../../../../../../../../../features/fees/fee-rate/fee-rate-list-items';
import { getFeeRateDetailPath } from '../../../../../../../../../../../../features/fees/fee-rate/fee-rate-routing';
import { FeeRateStatusPill } from '../../../../../../../../../../../../features/fees/fee-rate/fee-rate-status-pill';
import { FeeRateUpdateDrawer } from '../../../../../../../../../../../../features/fees/fee-rate/fee-rate-update-drawer';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/finance/_finance/middle-money/fee-rate/_fee-rate/$feeType/_$feeType/$effectiveDate',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `fee-rate-detail-${params.feeType}-${params.effectiveDate}`,
        label: 'Fee Rate',
        to: getFeeRateDetailPath(params.feeType, params.effectiveDate),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Fee Rate'),
  component: FeeRateDetail,
});

function getListItems(data: IxcFeesV1FeeRateState | undefined) {
  const listItems = getFeeRateStateListItems(data);

  return {
    general: {
      left: [
        listItems.feeTypePill,
        listItems.data.mapCode,
        listItems.data.firmMapCode,
        listItems.data.rate,
        listItems.data.maximum,
      ] satisfies ReadonlyListItem[],
      right: [listItems.metadata.createdAt, listItems.metadata.updatedAt, listItems.effectiveDate] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(feeType: IxcFeesV1FeeType, effectiveDate: string, status: IxcFeesV1FeeRateStatus | undefined): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: FeeRateEventDrawer,
        props: { request: { feeType, effectiveDate } },
      },
    },
    actions: [
      ...(status === IxcFeesV1FeeRateStatus.Active
        ? [
            {
              id: 'edit',
              icon: <EditIcon aria-hidden />,
              label: 'Edit',
              interaction: {
                type: 'drawer',
                drawer: { Component: FeeRateUpdateDrawer, props: { feeType, effectiveDate } },
              },
              requiresWrite: true,
            } as const,
          ]
        : []),
    ],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load fee rate', 'view this fee rate');

function FeeRateDetail() {
  const { feeType, effectiveDate } = Route.useParams();
  const request = { feeType, effectiveDate } as const;
  const { data, error, isPending } = useIxcFeesV1FeeRateQueryServiceFeeRateGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.feeRate), [data?.feeRate]);
  const menuItems = useMemo(() => getMenuItems(feeType, effectiveDate, data?.feeRate?.status), [feeType, effectiveDate, data?.feeRate?.status]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <FeeRateStatusPill isPending={showPending} size="large" status={data?.feeRate?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <FeeRateEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
